import {
  CancelledError,
  skipToken,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
  type QueryObserverResult,
} from '@tanstack/react-query';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';

type UseRawData = {
  isLoading: boolean;
  fetchRawData: () => Promise<string | null>;
};

const useRawData = (): UseRawData => {
  const queryClient = useQueryClient();
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();

  // enabledをfalseにすることで自動で取得が行われないようにし、データ取得の関数のみ提供する
  const { isFetching, refetch } = useQuery({
    queryKey: ['rawData', currentSimDataId, apiFilter],
    queryFn: currentSimDataId
      ? async ({ signal }: QueryFunctionContext): Promise<string> => {
          return dataApiClient.downloadCsvData(
            currentSimDataId,
            apiFilter,
            signal
          );
        }
      : skipToken,
    enabled: false,
    gcTime: 0,
    meta: { dataType: 'others' },
  });

  const fetchRawData = async (): Promise<string | null> => {
    return refetch({ throwOnError: true })
      .then(({ data }: QueryObserverResult<string>): string | null => {
        return data ?? null;
      })
      .catch((error: unknown): null => {
        if (error instanceof CancelledError) {
          return null;
        }
        throw error;
      })
      .finally((): void => {
        // サイズが大きいためキャッシュは残さずにすぐに破棄する
        queryClient.removeQueries({
          queryKey: ['rawData', currentSimDataId, apiFilter],
        });
      });
  };

  return { isLoading: isFetching, fetchRawData: fetchRawData };
};

export { useRawData };
